import { Col, Row } from "react-bootstrap";
import { Modal } from "../../../../../../components/modal";
import { IModal } from "../../../../../../models/IModal";
import {
    PendencyList,
    PendencyListBody,
    PendencyListHeader,
    PendencyListItem,
    ReadyToPublishList,
} from "../batch-action-modal/styles";
import { ISelectedNode } from "../..";
import { useEffect, useState } from "react";
import { ITreeNode, ITreeNodeData } from "../../../../../../models/ITreeNodeData";
import { useTranslation } from "react-i18next";
import { TreeTablePublishable } from "../batch-action-modal/components";
import { BatchModalMessage } from "./styled";

interface IBatchInactivateMaterialModal extends IModal {
    selection: {
        disciplines: ISelectedNode[];
        items: ISelectedNode[];
        objects: number[];
        objectsComposed: ISelectedNode[];
    };
    nodes: any[];
}

export const BatchInactivateMaterialModal: React.FC<IBatchInactivateMaterialModal> = (props) => {
    const { t: translate } = useTranslation();
    const { selection, show, nodes, onHide, onSubmit } = props;

    // List
    const [nodeSelection, setNodeSelection] = useState<ITreeNode[]>();
    const [pendencies, setPendencies] = useState<ITreeNodeData[]>();
    const [canPublish, setCanPublish] = useState<ITreeNode[]>();

    // Passo a passo do modal
    const [actionStep, setActionStep] = useState(0);

    // Atualiza o state quando a seleção muda
    useEffect(() => {
        updateNodeSelection();
    }, [selection, nodes]);

    useEffect(() => {
        updatePendencyList();
    }, [nodeSelection]);

    /**
     *  Atualiza lista de materiais que serão publicados quando chego no passo final
     */
    useEffect(() => {
        if (pendencies && pendencies.length > 0) {
            let publishList: ITreeNode[] = [];

            // adicionar somente os materiais sem pendencia a lista de publicação
            nodeSelection?.forEach((node) => {
                let hasPendency = pendencies.some((pendency) => pendency?.id === node?.data?.id);

                if (!hasPendency) {
                    publishList.push(node);
                }
            });

            setCanPublish(reNest(publishList));
            setActionStep(0);
        } else {
            setCanPublish(reNest(nodeSelection!));
            setActionStep(1);
        }
    }, [pendencies]);

    /**
     * Navega entre os passos do processo de publicação
     */
    const handleStepForward = () => {
        if (actionStep === 1) {
            onSubmit!();
            onHide();
        } else if (pendencies && pendencies.length > 0) {
            setActionStep(actionStep + 1);
        } else {
            setActionStep(1);
        }
    };

    /**
     * Extrai os nodes da tabela que estão selecionados e armazena no state nodeSelection
     */
    const updateNodeSelection = () => {
        if (nodes && selection) {
            let newNodeSelection: any[] = [];
            // puxar os nodes de cada item dentro de selection e colocar no state nodeSelection
            if (selection.disciplines?.length > 0) {
                selection.disciplines.forEach((selectedDiscipline) => {
                    // remover children do objeto - crio uma cópia
                    let disciplineInNodes = nodes.find((aNode) => aNode.data.id == selectedDiscipline.id);

                    if (disciplineInNodes) {
                        // veio da seleção natural
                        let disciplineCopy = { ...disciplineInNodes };
                        disciplineCopy.children = [];
                        newNodeSelection.push(disciplineCopy);
                    } else {
                        // veio do selecionar todos
                        newNodeSelection.push({
                            key: selectedDiscipline.title,
                            data: {
                                id: selectedDiscipline.id,
                                title: selectedDiscipline.title,
                                can_publish: selectedDiscipline.can_publish,
                                category: selectedDiscipline.category,
                                is_published: selectedDiscipline.is_published,
                                is_published_store: selectedDiscipline.is_published_store,
                                type: "Discipline",
                            },
                            leaf: false,
                        });
                    }
                });
            }
            if (selection.items?.length > 0) {
                selection.items.forEach((selectedItem) => {
                    // passar por todos nodes, checando se o id selecionado esta ali dentro, se estive, extrair
                    let foundInTree = false;
                    nodes.forEach((node) => {
                        if (hasMatchingId(node, selectedItem.id)) {
                            foundInTree = true;
                            let itemInNodes = {
                                ...extractMatchingIdFrom(node, selectedItem.id)!,
                            };
                            itemInNodes.children = [];

                            newNodeSelection.push(itemInNodes);
                        }
                    });

                    if (!foundInTree) {
                        // veio do selecionar todos
                        newNodeSelection.push({
                            key: selectedItem.title,
                            data: {
                                id: selectedItem.id,
                                title: selectedItem.title,
                                can_publish: selectedItem.can_publish,
                                category: selectedItem.category,
                                type: "Item",
                            },
                            leaf: false,
                        });
                    }
                });
            }
            if (selection.objects?.length > 0) {
                selection.objects.forEach((selectedObject) => {
                    // passar por todos nodes, checando se o id selecionado esta ali dentro, se estive, extrair
                    nodes.forEach((node) => {
                        if (hasMatchingId(node, selectedObject)) {
                            newNodeSelection.push(extractMatchingIdFrom(node, selectedObject)!);
                        }
                    });
                });
            }
            if (selection.objectsComposed?.length > 0) {
                selection.objectsComposed.forEach((selectedObject) => {
                    let alreadyParsed = newNodeSelection.findIndex((item) => item.id === selectedObject.id);

                    if (alreadyParsed === -1) {
                        newNodeSelection.push({
                            key: selectedObject.title!,
                            data: {
                                id: selectedObject.id,
                                title: selectedObject.title!,
                                can_publish: selectedObject.can_publish!,
                                category: selectedObject.category,
                                type: "Media",
                            },
                            leaf: true,
                        });
                    }
                });
            }

            setNodeSelection(newNodeSelection);
        }
    };

    /**
     * Extrai os elementos que não podem ser inativados da lista de selecionados
     */
    const updatePendencyList = () => {
        if (nodeSelection?.length) {
            // crio um set para nao criar duplicatas
            let pendenciesList = new Set<ITreeNodeData>();

            // Verificar se está publicado
            nodeSelection.forEach((node) => {
                if (node.data?.is_published || node.data?.is_published_store) {
                    pendenciesList.add({ ...node.data, reason: "is_publish" });
                }
            });
            setPendencies(Array.from(pendenciesList));
        }
    };

    /**
     * Procura recursivamente por um id dentro de um node
     */
    const hasMatchingId = (node: ITreeNode, id: number): boolean => {
        if (node.data.id === id) {
            return true;
        } else {
            if (node.children && node.children.length > 0) {
                return node.children.some((child) => hasMatchingId(child, id));
            } else {
                return false;
            }
        }
    };

    /**
     * Extrai um node com a id indicada de dentro de outro node
     * Quando usado em conjunto com hasMatchingId garante um resultado, caso contrário, retorna null quando a id não é encontrada
     */
    const extractMatchingIdFrom = (node: ITreeNode, id: number): ITreeNode | null => {
        if (node.data.id === id) {
            return node;
        } else {
            if (node.children && node.children.length > 0) {
                let matchingNode = null;
                node.children.forEach((child) => {
                    try {
                        matchingNode = extractMatchingIdFrom(child, id);
                    } catch (e) {}
                });
                return matchingNode;
            } else {
                throw new Error(`no matching id`);
            }
        }
    };

    /**
     * Re-aninho os itens selecionados, colocando objetos e unidades dentro do [children] de seus pais
     * @returns
     */
    const reNest = (nodeList: ITreeNode[]): ITreeNode[] => {
        let reNestedList: ITreeNode[] = [];

        // para cada node, verificar se ele é filho de alguém na lista
        if (nodeList && nodeList.length > 0) {
            // começo adicionando as disciplinas ao primeiro nível
            reNestedList.push(...nodeList.filter((aDiscipline) => aDiscipline.data?.type === `Discipline`));

            // unidades e temas podem ter pai
            let secondLevelItems = nodeList.filter((node) => node.data?.type === `Item` || node.data?.type === `Theme`);

            secondLevelItems.forEach((node) => {
                // procurar pela disciplina mãe, se existir, adicionar este elemento como child
                let possibleParent = reNestedList.find(
                    (parent) => parent.data?.id && parent.data?.id === node.data?.discipline
                );

                if (possibleParent) {
                    if (possibleParent.children) {
                        possibleParent.children.push(node);
                    } else {
                        possibleParent.children = [node];
                    }
                } else {
                    // caso não tenha pai, vai para a root da lista
                    // indico que não tem pai, para não ter identação na tabela depois
                    node.independent = true;
                    reNestedList.push(node);
                }
            });

            // objetos
            let thirdLevelItems = nodeList.filter((node) => node.data?.type === `Media`);
            thirdLevelItems.forEach((node) => {
                // procurar pela disciplina mãe, se existir, procurar pela unidade mãe
                let possibleGrandParent = reNestedList.find(
                    (parent) => parent.data?.id && parent.data?.id === node.data?.discipline
                );
                if (possibleGrandParent && possibleGrandParent.children?.length) {
                    // a disciplina mãe deste objeto está na lista, buscar pela unidade
                    let possibleParent = possibleGrandParent.children.find(
                        (parent) => parent.data?.id && parent.data?.id === node.data?.discipline
                    );

                    if (possibleParent) {
                        if (possibleParent.children) {
                            possibleParent.children.push(node);
                        } else {
                            possibleParent.children = [node];
                        }
                    } else {
                        possibleGrandParent.children.push(node);
                    }
                } else {
                    // pode ser que ele esteja dentro de um tema ou unidade mas nao de uma disciplina
                    let possibleParent = reNestedList.find(
                        (parent) => parent.data?.id && parent.data?.id === node.data?.item
                    );
                    if (possibleParent) {
                        if (possibleParent.children) {
                            possibleParent.children.push(node);
                        } else {
                            possibleParent.children = [node];
                        }
                    } else {
                        // indico que não tem pai, para não ter identação na tabela depois
                        node.independent = true;
                        reNestedList.push(node);
                    }
                }
            });
        }
        return reNestedList;
    };

    const reasons = (reason: string): string => {
        return (
            {
                is_publish: "Material se encontra publicado",
            }[reason] ?? ""
        );
    };

    return (
        <>
            <Modal
                show={show}
                variant={"form-modal-default"}
                loading={false}
                onHide={onHide}
                onSubmit={canPublish && canPublish?.length > 0 ? handleStepForward : undefined}
                headerTitle={`Inativar materiais em lote`}
                submitButtonText={actionStep === 1 ? "Inativar" : "Continuar"}
            >
                {actionStep === 0 && (
                    <Row>
                        <BatchModalMessage xs={12}>
                            {canPublish && canPublish.length === 0 ? (
                                <>
                                    <h3>Não é possível inativar os materiais selecionados no momento.</h3>
                                    <p>
                                        Os materiais abaixo não podem ser inativados, pois estão atualmente publicados.
                                        Para prosseguir com essa ação, será necessário primeiro despublicar esses
                                        materiais e, em seguida, tentar novamente a inativação.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3>Não é possível inativar os seguintes materiais no momento.</h3>
                                    <p>
                                        Os materiais listados abaixo não podem ser inativados, pois estão atualmente
                                        publicados. Para prosseguir com essa ação, será necessário primeiro despublicar
                                        esses materiais e, em seguida, tentar novamente a inativação.
                                    </p>
                                </>
                            )}
                            <p>Materiais que não serão inativados:</p>
                        </BatchModalMessage>
                        <Col>
                            <PendencyList>
                                <PendencyListHeader hasReason={true}>
                                    <span>Material</span>
                                    <span>Tipo</span>
                                    <span></span>
                                    <span>Motivo</span>
                                </PendencyListHeader>
                                <PendencyListBody>
                                    {pendencies?.map((pendency) => (
                                        <PendencyListItem key={pendency.id} hasReason={true}>
                                            <span>{pendency.title}</span>
                                            <span>{translate(`manager.type.${pendency.type.toLowerCase()}`)}</span>
                                            <span></span>
                                            <span> {reasons(pendency.reason!)}</span>
                                        </PendencyListItem>
                                    ))}
                                </PendencyListBody>
                            </PendencyList>
                        </Col>
                    </Row>
                )}
                {actionStep === 1 && (
                    <Row>
                        <ReadyToPublishList>
                            <BatchModalMessage xs={12}>
                                <h3 style={{fontWeight: 400}}>
                                    {pendencies && pendencies.length > 0 ? "Somente os" : "Os"} seguintes itens serão
                                    inativados.
                                </h3>
                            </BatchModalMessage>

                            <TreeTablePublishable nodes={canPublish}></TreeTablePublishable>
                        </ReadyToPublishList>
                    </Row>
                )}
            </Modal>
        </>
    );
};
